import * as ExcelJS from "exceljs";
import { addJustupBranding, setJustupMetadata } from "../../../../shared/utils/excel-branding.util";
import { AchatEvolutionDialogData } from "./stats-achat-evolution-dialog.component";

const C = {
  headerBg: "FF1B2A4A", headerFont: "FFFFFFFF",
  groupBg: "FFE8EDF3", groupFont: "FF1B2A4A",
  subBg: "FFF5F7FA", border: "FFD0D5DD",
  statGreen: "FFE6FFF5", statPurple: "FFF0E6FF", statOrange: "FFFFF0E0",
  titleColor: "FF1B2A4A",
};

function colLetter(col: number): string {
  let s = "", c = col;
  while (c > 0) { c--; s = String.fromCharCode(65 + (c % 26)) + s; c = Math.floor(c / 26); }
  return s;
}
function cellRef(col: number, row: number): string { return `${colLetter(col)}${row}`; }

function headerStyle(cell: ExcelJS.Cell) {
  cell.font = { bold: true, size: 10, color: { argb: C.headerFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  cell.border = { bottom: { style: "medium", color: { argb: C.headerBg } } };
}
function statStyle(cell: ExcelJS.Cell, bg: string) {
  cell.font = { bold: true, size: 11 };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  const b = { style: "thin" as const, color: { argb: C.border } };
  cell.border = { top: b, bottom: b, left: b, right: b };
}

function fmtEur(n: number): string {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

function buildCommandeSheet(wb: ExcelJS.Workbook, data: AchatEvolutionDialogData) {
  const ws = wb.addWorksheet("Par commande", { views: [{ state: "frozen", ySplit: 5 }] });

  const COL = { numero: 1, date: 2, lot: 3, qty: 4, puAchat: 5, cout: 6 };
  [18, 12, 20, 12, 14, 14].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells("A1:F1");
  const tc = ws.getCell("A1");
  tc.value = `Évolution prix d'achat — ${data.articleDenomination} (${data.tiersDenomination})`;
  tc.font = { bold: true, size: 14, color: { argb: C.titleColor } };
  ws.getRow(1).height = 26;

  const totalQty = data.commandes.reduce((s, c) => s + c.qtyUG, 0);
  const totalCout = data.commandes.reduce((s, c) => s + c.coutTotal, 0);
  const puMoyen = totalQty !== 0 ? totalCout / totalQty : 0;

  ws.mergeCells("A3:B3"); ws.mergeCells("C3:D3"); ws.mergeCells("E3:F3");
  const s1 = ws.getCell("A3");
  s1.value = `PU moyen : ${puMoyen.toFixed(2)} € / ${data.articleUniteGestion || "UG"}`;
  statStyle(s1, C.statPurple);
  const s2 = ws.getCell("C3");
  s2.value = `Qté totale : ${totalQty.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} ${data.articleUniteGestion || "UG"}`;
  statStyle(s2, C.statGreen);
  const s3 = ws.getCell("E3");
  s3.value = `Coût total : ${fmtEur(totalCout)}`;
  statStyle(s3, C.statOrange);
  ws.getRow(3).height = 26;

  const headers = ["N° Commande", "Date", "Lot", "Qté", `PU achat (€)`, "Coût (€)"];
  headers.forEach((h, i) => { const c = ws.getCell(5, i + 1); c.value = h; headerStyle(c); });
  ws.getRow(5).height = 22;
  ws.autoFilter = { from: { row: 5, column: 1 }, to: { row: 5, column: 6 } };

  const eurFmt = '#,##0.00\\ "€"';
  let row = 6;

  const sorted = [...data.commandes].sort((a, b) =>
    a.dateKey === b.dateKey ? a.numero.localeCompare(b.numero) : b.dateKey.localeCompare(a.dateKey),
  );

  for (const cmd of sorted) {
    const gr = ws.getRow(row);
    gr.getCell(COL.numero).value = cmd.numero;
    gr.getCell(COL.date).value = cmd.dateKey ? new Date(cmd.dateKey) : "";
    gr.getCell(COL.date).numFmt = "dd/mm/yyyy";
    gr.getCell(COL.lot).value = `${cmd.lots.length} lot${cmd.lots.length > 1 ? "s" : ""}`;
    gr.getCell(COL.qty).value = cmd.qtyUG;
    gr.getCell(COL.puAchat).value = cmd.puAchatMoyen;
    gr.getCell(COL.cout).value = { formula: `${cellRef(COL.qty, row)}*${cellRef(COL.puAchat, row)}`, result: cmd.coutTotal } as any;

    for (let c = 1; c <= 6; c++) {
      gr.getCell(c).font = { bold: true, size: 10, color: { argb: C.groupFont } };
      gr.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.groupBg } };
      gr.getCell(c).border = { bottom: { style: "thin", color: { argb: C.border } }, top: { style: "thin", color: { argb: C.border } } };
    }
    gr.getCell(COL.qty).numFmt = "#,##0.00";
    [COL.puAchat, COL.cout].forEach((c) => (gr.getCell(c).numFmt = eurFmt));
    row++;

    for (const lot of cmd.lots) {
      const sr = ws.getRow(row);
      sr.getCell(COL.lot).value = lot.lotNumero || "— inconnu —";
      sr.getCell(COL.qty).value = lot.qtyUG;
      sr.getCell(COL.puAchat).value = lot.puAchat;
      sr.getCell(COL.cout).value = { formula: `${cellRef(COL.qty, row)}*${cellRef(COL.puAchat, row)}`, result: lot.coutTotal } as any;

      for (let c = 1; c <= 6; c++) {
        sr.getCell(c).font = { size: 9, italic: true };
        sr.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.subBg } };
        sr.getCell(c).border = { bottom: { style: "hair", color: { argb: C.border } } };
      }
      sr.getCell(COL.qty).numFmt = "#,##0.00";
      [COL.puAchat, COL.cout].forEach((c) => (sr.getCell(c).numFmt = eurFmt));
      row++;
    }
  }
}

function buildMonthSheet(wb: ExcelJS.Workbook, data: AchatEvolutionDialogData) {
  const ws = wb.addWorksheet("Par mois", { views: [{ state: "frozen", ySplit: 5 }] });

  const COL = { mois: 1, nb: 2, qty: 3, pu: 4, cout: 5 };
  [18, 12, 14, 14, 14].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells("A1:E1");
  const tc = ws.getCell("A1");
  tc.value = `Évolution prix d'achat par mois — ${data.articleDenomination}`;
  tc.font = { bold: true, size: 14, color: { argb: C.titleColor } };
  ws.getRow(1).height = 26;

  const map = new Map<string, { qty: number; cout: number; nb: number }>();
  for (const c of data.commandes) {
    const d = new Date(c.dateKey);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (!map.has(key)) map.set(key, { qty: 0, cout: 0, nb: 0 });
    const m = map.get(key)!;
    m.qty += c.qtyUG;
    m.cout += c.coutTotal;
    m.nb++;
  }
  const rows = [...map.entries()]
    .map(([key, v]) => ({
      key,
      nb: v.nb,
      qty: v.qty,
      pu: v.qty !== 0 ? v.cout / v.qty : 0,
      cout: v.cout,
    }))
    .sort((a, b) => b.key.localeCompare(a.key));

  const headers = ["Mois", "Nb commandes", "Qté", "PU moyen (€)", "Coût (€)"];
  headers.forEach((h, i) => { const c = ws.getCell(5, i + 1); c.value = h; headerStyle(c); });
  ws.getRow(5).height = 22;
  ws.autoFilter = { from: { row: 5, column: 1 }, to: { row: 5, column: 5 } };

  const eurFmt = '#,##0.00\\ "€"';
  let row = 6;

  for (const m of rows) {
    const r = ws.getRow(row);
    r.getCell(COL.mois).value = m.key;
    r.getCell(COL.nb).value = m.nb;
    r.getCell(COL.qty).value = m.qty;
    r.getCell(COL.pu).value = m.pu;
    r.getCell(COL.cout).value = { formula: `${cellRef(COL.qty, row)}*${cellRef(COL.pu, row)}`, result: m.cout } as any;

    for (let c = 1; c <= 5; c++) {
      r.getCell(c).font = { size: 10 };
      r.getCell(c).border = { bottom: { style: "hair", color: { argb: C.border } } };
    }
    r.getCell(COL.mois).font = { bold: true, size: 10, color: { argb: C.groupFont } };
    [COL.pu, COL.cout].forEach((c) => (r.getCell(c).numFmt = eurFmt));
    r.getCell(COL.qty).numFmt = "#,##0.00";
    r.getCell(COL.nb).numFmt = "0";
    row++;
  }
}

export async function exportAchatEvolutionExcel(
  data: AchatEvolutionDialogData,
  ncs?: import("../../../non-conformite/models/non-conformite.model").NonConformite[],
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb);

  buildCommandeSheet(wb, data);
  await addJustupBranding(wb, wb.getWorksheet("Par commande")!, 6);

  buildMonthSheet(wb, data);
  await addJustupBranding(wb, wb.getWorksheet("Par mois")!, 5);

  if (ncs && ncs.length > 0) {
    const { addNcSheet } = await import("../../../non-conformite/utils/nc-excel-sheet.utils");
    addNcSheet(wb, ncs, {
      contextLabel: `${data.tiersDenomination} · ${data.articleDenomination}`,
    });
  }

  const buffer = await wb.xlsx.writeBuffer() as ArrayBuffer;
  const blob = new Blob([new Uint8Array(buffer)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const slug = (data.articleDenomination || "article").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);
  const filename = `evolution-prix-achat-${slug}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 10000);
}
